import type { OrganizationMember } from "@common/dto/organization/organization-member.dto";
import type { WorkItemEdit, WorkRoleField } from "@api/workItem.api";
import { UserAvatar } from "@components/avatar/UserAvatar";
import type { ClientsWorkCommandOptions } from "@components/commands/lib";
import type { ClientsWorkOverviewWorkRow } from "../clientsWorkOverviewViewConfig";
import {
    getWorkMemberName,
    getWorkTeamCandidates,
    type WorkEditability,
} from "../editing/workItemEditing";
import { getWorkRoleHolderId } from "../editing/WorkRolePicker";

type MemberOptions = NonNullable<ClientsWorkCommandOptions["memberOptions"]>;
type DateSteps = NonNullable<ClientsWorkCommandOptions["dateSteps"]>;

/** The day every row shares, else none. */
const getSharedDate = (dates: Array<string | null>) =>
    dates.every((date) => date === dates[0])
        ? (dates[0] ?? undefined)
        : undefined;

const getDates = (dates: Array<string | null>) =>
    dates.filter((date): date is string => date !== null).sort();

/**
 * Cmd+K's work date steps for open `rows`: a day that keeps Work start on
 * or before Work end on every one of them.
 */
export const getWorkDateSteps = ({
    rows,
    onEdit,
}: {
    rows: ClientsWorkOverviewWorkRow[];
    onEdit: (edit: WorkItemEdit) => void;
}): DateSteps => {
    const starts = rows.map((row) => row.workStartDate);
    const ends = rows.map((row) => row.workDeliveryDate);
    const sharedStart = getSharedDate(starts);
    const sharedEnd = getSharedDate(ends);
    return {
        workStartDate: {
            value: sharedStart,
            maxDate: getDates(ends)[0],
            onSelect: (date) => {
                if (date !== sharedStart) {
                    onEdit({ field: "workStartDate", date });
                }
            },
        },
        workDeliveryDate: {
            value: sharedEnd,
            minDate: getDates(starts).at(-1),
            onSelect: (date) => {
                if (date !== sharedEnd) {
                    onEdit({ field: "workDeliveryDate", date });
                }
            },
        },
    };
};

/** True when `userId` alone holds `field`'s open tasks on every row. */
const isEveryRowHeldBy = ({
    rows,
    field,
    userId,
}: {
    rows: ClientsWorkOverviewWorkRow[];
    field: WorkRoleField;
    userId: number;
}) => rows.every((row) => getWorkRoleHolderId(row, field) === userId);

/**
 * Cmd+K's people per role: those with access to the client of every row
 * the role can change on. Roles with no open tasks on any row are left out.
 */
export const getWorkMemberOptions = ({
    rows,
    members,
    organizationId,
    isRoleEditable,
    onEdit,
}: {
    rows: ClientsWorkOverviewWorkRow[];
    members: OrganizationMember[];
    organizationId: string | null;
    isRoleEditable: WorkEditability["isRoleEditable"];
    onEdit: (edit: WorkItemEdit) => void;
}): MemberOptions => {
    const toOptions = (field: WorkRoleField) => {
        const editableRows = rows.filter((row) => isRoleEditable(row, field));
        const workspaceIds = editableRows.map((row) => row.workspaceId);
        if (workspaceIds.length === 0) {
            return undefined;
        }
        const candidates = getWorkTeamCandidates({
            members,
            workspaceIds: [...new Set(workspaceIds)],
            organizationId,
        });
        return candidates.map((member) => {
            const name = getWorkMemberName(member);
            return {
                id: `work-${field}-${member.userId}`,
                label: name,
                keywords: [name.toLowerCase(), member.email],
                icon: <UserAvatar user={{ ...member, name }} size="xs" />,
                onSelect: () => {
                    if (
                        !isEveryRowHeldBy({
                            rows: editableRows,
                            field,
                            userId: member.userId,
                        })
                    ) {
                        onEdit({ field, member });
                    }
                },
            };
        });
    };
    return {
        preparers: toOptions("preparers"),
        reviewers: toOptions("reviewers"),
        managers: toOptions("managers"),
    };
};
